// socketEvents.js - Manages all socket events for the broker PWA

import SocketHandler from './SocketHandler';

class SocketEventManager {
  constructor() {
    this.socketHandler = SocketHandler.getInstance();
    this.eventHandlers = new Map();
    this.setupEventListeners();
  }

  // Setup all event listeners
  setupEventListeners() {
    const { socket } = this.socketHandler;
    if (!socket) return;

    // Listen for broker list updates
    socket.on('brokers', (data) => {
      this.triggerEvent('brokers', data);
    });

    // Listen for player list updates
    socket.on('players', (data) => {
      this.triggerEvent('players', data);
    });

    // Listen for new player notifications
    socket.on('new_player', (data) => {
      this.triggerEvent('new_player', data);
    });

    // Listen for join broker room requests
    socket.on('join_broker_room', (data) => {
      this.triggerEvent('join_broker_room', data);
    });

    // Listen for commands
    socket.on('command', (data) => {
      this.triggerEvent('command', data);
    });

    // Listen for public messages
    socket.on('public_message', (data) => {
      this.triggerEvent('public_message', data);
    });

    // Listen for server heartbeat
    socket.on('server_active', (data) => {
      this.triggerEvent('server_active', data);
    });

    // Listen for broker exists error
    socket.on('broker_exists', (data) => {
      this.triggerEvent('broker_exists', data);
    });
  }

  // Register event handler
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  // Remove event handler
  off(event, handler) {
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  // Trigger event handlers
  triggerEvent(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  // Broker methods
  joinBroker(brokerId, players = []) {
    const { socket } = this.socketHandler;
    socket.emit('broker_join', { id: brokerId, players });
  }

  getBrokers() {
    const { socket } = this.socketHandler;
    socket.emit('get_brokers');
  }

  getPlayers() {
    const { socket } = this.socketHandler;
    socket.emit('get_players');
  }

  // Player methods
  joinPlayer(playerId) {
    const { socket } = this.socketHandler;
    socket.emit('player_join', { id: playerId });
  }

  sendJoinRequest(brokerId, playerIds) {
    const { socket } = this.socketHandler;
    socket.emit('send_join_request', { id: brokerId, players: playerIds });
  }

  // Command methods
  sendCommand(hardwareId, commandData) {
    const { socket } = this.socketHandler;
    socket.emit('send_command', { hardware_id: hardwareId, ...commandData });
  }

  // Message methods
  sendPublicMessage(message) {
    const { socket } = this.socketHandler;
    socket.emit('public_message', message);
  }

  // Heartbeat
  sendHeartbeat() {
    const { socket } = this.socketHandler;
    socket.emit('heartbeat');
  }

  // Connection management
  connect() {
    this.socketHandler.connect();
    // Re-setup listeners after connection
    const { socket } = this.socketHandler;
    socket.on('connect', () => {
      this.setupEventListeners();
    });
  }

  disconnect() {
    this.socketHandler.disconnect();
  }

  // Get connection state
  getConnectionState() {
    return this.socketHandler.state;
  }

  // Set connection state handler
  setConnectionStateHandler(handler) {
    this.socketHandler.setConnectionStateHandler(handler);
  }
}

// Create singleton instance
const socketEventManager = new SocketEventManager();

export default socketEventManager; 